import { ChangeEvent, useContext } from "react";
import { Button } from "../../components";
import { FileTypes, ToastVariants } from "../../models";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { DeleteOutline } from "@mui/icons-material";
import { colors } from "../../utils/constants";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { AppDispatch, RootState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { LinearProgress } from "@mui/material";
import { setFile } from "../../redux/slices/fileUploadSlice";

const allowedFiles = [FileTypes.CSV, FileTypes.XLSX];

const FileUploadTemplate = ({
  handleCancel,
  getTemplateData,
  showDemoFile,
  formats,
  sampleFile,
}: {
  handleCancel: (val?: boolean) => void;
  getTemplateData: (val?: boolean) => void;
  showDemoFile?: boolean;
  formats?: string;
  sampleFile?: string;
}) => {
  const toast = useContext(ToastContext);
  const dispatch = useDispatch<AppDispatch>();
  const { file, uploading, progress } = useSelector((state: RootState) => state.fileUpload);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (allowedFiles.includes(selectedFile.type as FileTypes)) {
        dispatch(setFile(selectedFile));
      } else {
        toast?.openToast("Invalid file format", 2000, ToastVariants.Warn);
      }
    }
  };

  const cancel = (val?: boolean) => {
    handleCancel(val);
  };

  return (
    <div className="mx-auto p-4 w-full">
      {showDemoFile && (
        <p className="text-base mb-3 text-docsigna-blue-dark text-center">
          Download the{" "}
          <a href={sampleFile} target="_blank" download className="text-red-500 underline hover:text-docsigna-blue-dark" rel="noreferrer">
            sample file
          </a>{" "}
          to view the required format and fields.
        </p>
      )}
      <p className="text-base mb-3 text-docsigna-blue-dark text-center">Upload your File Here</p>

      <div className="text-center bg-transparent border border-docsigna-blue-dark rounded my-5 relative">
        <input
          type="file"
          className="text-base rounded-md border-gray-300 focus:border-docsigna-purple w-full h-full py-6 px-4 opacity-0 relative z-10"
          onChange={handleFileChange}
        />
        <div className="w-full h-full py-6 px-4 absolute top-0 flex items-center justify-center">
          <AttachFileIcon className="rotate-[45deg]" />
          <p className="ml-1 mt-1">Upload here</p>
        </div>
      </div>

      {progress !== 100 && uploading && (
        <div className="px-2 w-full">
          <LinearProgress variant="determinate" value={progress || 0} />
        </div>
      )}

      {file && (
        <div className="text-center my-5 relative flex items-center justify-between">
          <p>{file.name}</p>
          <div className="cursor-pointer" onClick={() => dispatch(setFile(undefined))}>
            <DeleteOutline htmlColor={colors.Orange} />
          </div>
        </div>
      )}
      {formats && <div className="-mt-3">{formats}</div>}
      <p className="mb-3 mt-1 text-docsigna-blue-dark">
        The CSV file must use a <b>pipe symbol (|)</b> as its delimiter.
      </p>

      <div className="flex justify-end gap-4 absolute right-[34px] bottom-[38px]">
        <Button disabled={uploading} onClick={() => cancel()} text="Cancel" variant="Secondary" />
        <Button disabled={uploading} onClick={getTemplateData} text="Confirm" />
      </div>
    </div>
  );
};

export default FileUploadTemplate;
